"use client";

import { Icon } from "@commerce-frontend/ui/base/Icon/Icon";
import { useAlternates } from "@commerce-frontend/ui/hooks/alternates";
import { cn } from "@commerce-frontend/ui/utils/classNames";
import { type LocaleWithStaticSiteConfig } from "@commerce-shared/site-config";
import { countryOf, flagPath, getDisplayName } from "@commerce-shared/utility";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { memo } from "react";
import TopBar from "./TopBar";

type Props = {
	onBack: () => void;
	locale: string;
	localesWithsStaticSiteConfig: LocaleWithStaticSiteConfig[];
};

export const LanguageSelect = memo(
	({ onBack, locale: currentLocale, localesWithsStaticSiteConfig }: Props) => {
		const t = useTranslations("Navigation");

		const alternates = useAlternates(
			currentLocale,
			localesWithsStaticSiteConfig,
		);

		return (
			<>
				<TopBar onBack={onBack} title={t("mobileLanguageTitle")} />

				<div className="max-h-screen flex-1 overflow-y-scroll">
					<nav className="p-2">
						<ul className="m-0 px-2 py-0">
							{localesWithsStaticSiteConfig.map(
								(localeWithStaticSiteConfig, index) => {
									const locale = localeWithStaticSiteConfig.locale;
									const staticSiteConfig =
										localeWithStaticSiteConfig.staticSiteConfig;
									const alternate = alternates.find((a) => a.locale === locale);

									if (!alternate) {
										return null;
									}

									const displayName = getDisplayName(staticSiteConfig, locale);
									return (
										<li key={index} className="border-b last:border-b-0">
											<a
												className={cn(
													"border-grap-500 relative flex w-full items-center gap-2 border-b px-0 py-4 text-sm text-blue-300 ring-offset-0 last:border-b-0",
													locale === currentLocale && "font-bold text-blue-500",
												)}
												href={alternate?.href}
											>
												<Image
													className="size-[1.125rem] rounded-full object-cover"
													src={flagPath(countryOf(locale))}
													width="18"
													height="18"
													alt={`flag of ${countryOf(locale)}`}
													unoptimized
												/>

												<span className="capitalize">{displayName}</span>

												{locale === currentLocale ? (
													<Icon
														icon="check"
														className="absolute right-1 size-5 bg-background font-bold"
													/>
												) : undefined}
											</a>
										</li>
									);
								},
							)}
						</ul>
					</nav>
				</div>
			</>
		);
	},
);

LanguageSelect.displayName = "LanguageSelect";
